import type { ApiResponse } from "../types/api";
import type { BikeCsvImportResult } from "../types/bike";
import type { CountryStatus } from "../types/country";
import type {
  ImportPreview,
  ImportJob,
  ImportConfig,
  ImportError,
} from "../types/import";
import type { HeatmapData } from "../types/route";
import type {
  ImpressionJob,
  ImpressionJobListResponse,
  JobAnalytics,
  ImpressionJobStats,
} from "../types/impressionJob";
import type {
  CreateReportJobRequest,
  ReportJob,
  ReportJobListResponse,
} from "../types/reportJob";
import type {
  CreateFleetUserResponse,
  Fleet,
  User,
  UserRole,
} from "../types/auth";
import { clientCsvPreview } from "./csvPreview";
import { config } from "../config/env";

const API_BASE_URL = config.apiBaseUrl;

// Function to get auth token from localStorage
const getAuthToken = (): string | null => {
  try {
    const authStore = localStorage.getItem("bike-tracker-auth");
    if (authStore) {
      const parsed = JSON.parse(authStore);
      return parsed?.state?.token || null;
    }
    return null;
  } catch {
    return null;
  }
};

class ApiService {
  private async fetchAllPaginated<T>(
    fetchPage: (
      page: number,
      limit: number,
    ) => Promise<{ items: T[]; totalPages: number }>,
    pageSize: number = 1000,
  ): Promise<T[]> {
    const allItems: T[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const { items, totalPages: nextTotalPages } = await fetchPage(
        page,
        pageSize,
      );
      allItems.push(...items);
      totalPages = nextTotalPages;
      page += 1;
    } while (page <= totalPages);

    return allItems;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = getAuthToken();

    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      mode: "cors",
      credentials: "include",
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data: ApiResponse<T> = await response.json();

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          // Token expired or invalid, clear auth state
          localStorage.removeItem("bike-tracker-auth");
          window.location.reload();
          throw new ApiError(
            "Session expired. Please log in again.",
            response.status,
            data.timestamp,
          );
        }

        throw new ApiError(
          data.message || "An error occurred",
          response.status,
          data.timestamp,
        );
      }

      return data.data!;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError("Network error", 0, new Date().toISOString());
    }
  }

  private async requestFormData<T>(
    endpoint: string,
    formData: FormData,
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = getAuthToken();

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      const data: ApiResponse<T> = await response.json();

      if (!response.ok) {
        throw new ApiError(
          data.message || "An error occurred",
          response.status,
          data.timestamp,
        );
      }

      return data.data!;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError("Network error", 0, new Date().toISOString());
    }
  }

  // Import endpoints
  async previewImport(
    file: File,
    forceServerSide: boolean = false,
  ): Promise<ImportPreview> {
    const fileSizeMB = file.size / (1024 * 1024);

    // Use client-side preview for files larger than 5MB unless forced to use server-side
    if (!forceServerSide && fileSizeMB > 5) {
      return clientCsvPreview.previewFile(file);
    }

    // Fallback to server-side preview for smaller files or when forced
    const formData = new FormData();
    formData.append("csvFile", file);

    return this.requestFormData("/import/preview", formData);
  }

  async previewImportClientSide(file: File): Promise<ImportPreview> {
    return clientCsvPreview.previewFile(file);
  }

  async startImport(
    file: File,
    config: ImportConfig,
  ): Promise<{ job_id: string }> {
    const formData = new FormData();
    formData.append("csvFile", file);
    formData.append("config", JSON.stringify(config));

    return this.requestFormData("/import/start", formData);
  }

  async getImportJobs(params?: {
    page?: number;
    limit?: number;
  }): Promise<{ jobs: ImportJob[] }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/import/jobs${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async getImportJob(jobId: string): Promise<ImportJob> {
    return this.request(`/import/jobs/${jobId}`);
  }

  async getImportErrors(
    jobId: string,
    params?: {
      limit?: number;
    },
  ): Promise<{ errors: ImportError[] }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/import/jobs/${jobId}/errors${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async cancelImportJob(jobId: string): Promise<void> {
    return this.request(`/import/jobs/${jobId}/cancel`, {
      method: "POST",
    });
  }

  async getImportStats(): Promise<{
    total_jobs: number;
    completed_jobs: number;
    failed_jobs: number;
    total_rows_processed: number;
    total_rows_successful: number;
    total_rows_failed: number;
  }> {
    return this.request("/import/stats");
  }

  // Bike endpoints
  async getBikes(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    category_id?: string;
    uncategorized_only?: boolean;
    include_hidden?: boolean;
    include_location?: boolean;
    sort_by?: string;
    sort_order?: "asc" | "desc" | "ASC" | "DESC";
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/bikes${queryParams.toString() ? `?${queryParams}` : ""}`;
    return this.request(endpoint);
  }

  async getAllBikes(params?: {
    status?: string;
    search?: string;
    category_id?: string;
    uncategorized_only?: boolean;
    include_hidden?: boolean;
    include_location?: boolean;
    sort_by?: string;
    sort_order?: "asc" | "desc" | "ASC" | "DESC";
  }) {
    const bikes = await this.fetchAllPaginated<unknown>(async (page, limit) => {
      const response = (await this.getBikes({
        ...params,
        page,
        limit,
      })) as {
        bikes: unknown[];
        pagination?: { totalPages?: number };
      };

      return {
        items: response.bikes || [],
        totalPages: response.pagination?.totalPages ?? 1,
      };
    });

    return {
      bikes,
      pagination: {
        total: bikes.length,
        page: 1,
        limit: bikes.length || 1,
        totalPages: 1,
      },
    };
  }

  async getBike(bikeId: string) {
    return this.request(`/bikes/${bikeId}`);
  }

  async createBike(bike: {
    bike_id: string;
    status?: "active" | "inactive" | "maintenance";
    category_id?: string | null;
    bike_sn?: string | null;
    max_speed?: number | null;
    comment?: string | null;
    primary_city_id?: string | null;
    ui_hidden?: boolean;
  }) {
    return this.request("/bikes", {
      method: "POST",
      body: JSON.stringify(bike),
    });
  }

  async updateBike(
    bikeId: string,
    updates: {
      status?: "active" | "inactive" | "maintenance";
      category_id?: string | null;
      bike_sn?: string | null;
      max_speed?: number | null;
      comment?: string | null;
      primary_city_id?: string | null;
      ui_hidden?: boolean;
    },
  ) {
    return this.request(`/bikes/${bikeId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteBike(bikeId: string) {
    return this.request(`/bikes/${bikeId}`, {
      method: "DELETE",
    });
  }

  async getBikeStatistics() {
    return this.request("/bikes/statistics");
  }

  async importBikesCsv(
    file: File,
    dryRun = false,
  ): Promise<BikeCsvImportResult> {
    const formData = new FormData();
    formData.append("csvFile", file);
    const query = dryRun ? "?dry_run=true" : "";
    return this.requestFormData(`/bikes/import${query}`, formData);
  }

  // GPS endpoints
  async getGpsHistory(
    bikeId: string,
    params?: {
      start_time?: string;
      end_time?: string;
      limit?: number;
      offset?: number;
    },
  ) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/gps/${bikeId}/history${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async getCurrentLocation(bikeId: string) {
    return this.request(`/gps/${bikeId}/current`);
  }

  async getBikeRoute(
    bikeId: string,
    params?: {
      start_time?: string;
      end_time?: string;
      simplify?: boolean;
    },
  ) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/gps/${bikeId}/route${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async getGpsCoordinates(params?: {
    bike_id?: string;
    start_time?: string;
    end_time?: string;
    limit?: number;
    offset?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/gps/coordinates${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async deleteGpsCoordinate(id: string) {
    return this.request(`/gps/coordinates/${id}`, {
      method: "DELETE",
    });
  }

  async bulkDeleteGpsCoordinates(ids: string[]) {
    return this.request("/gps/coordinates/bulk-delete", {
      method: "DELETE",
      body: JSON.stringify({ ids }),
    });
  }

  // Map endpoints
  async getBikeMarkers(params?: {
    north?: number;
    south?: number;
    east?: number;
    west?: number;
    include_inactive?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/maps/bikes${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async getMapRoute(
    bikeId: string,
    params?: {
      start_time?: string;
      end_time?: string;
      zoom_level?: number;
      max_points?: number;
    },
  ) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/maps/bikes/${bikeId}/route${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async getHeatmapData(params: {
    north: number;
    south: number;
    east: number;
    west: number;
    grid_size?: number;
    start_time?: string;
    end_time?: string;
    bike_ids?: string[];
  }) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        if (key === "bike_ids" && Array.isArray(value)) {
          // Handle bike_ids array - join with commas
          queryParams.append(key, value.join(","));
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });

    return this.request(`/maps/heatmap?${queryParams}`);
  }

  async searchNearbyBikes(params: {
    latitude: number;
    longitude: number;
    radius?: number;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    return this.request(`/maps/bikes/search?${queryParams}`);
  }

  async getMapStats() {
    return this.request("/maps/stats");
  }

  // Real-time endpoints
  async getRealtimeBikes(params?: {
    north?: number;
    south?: number;
    east?: number;
    west?: number;
    last_update?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/maps/realtime/bikes${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async getBikeStatus(bikeId: string) {
    return this.request(`/maps/realtime/bikes/${bikeId}/status`);
  }

  async getMovingBikes(params?: {
    north?: number;
    south?: number;
    east?: number;
    west?: number;
    min_speed?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/maps/realtime/moving${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  // Geofence endpoints
  async getGeofences() {
    return this.request("/maps/geofences");
  }

  async createGeofence(geofence: {
    name: string;
    type: "circle" | "polygon";
    coordinates: Array<{ latitude: number; longitude: number }>;
    radius?: number;
  }) {
    return this.request("/maps/geofences", {
      method: "POST",
      body: JSON.stringify(geofence),
    });
  }

  async getGeofenceEvents(timeWindow?: number) {
    const endpoint = timeWindow
      ? `/maps/geofences/events?time_window=${timeWindow}`
      : "/maps/geofences/events";
    return this.request(endpoint);
  }

  async getRouteHeatmapData(
    bikeIds: string[],
    startTime: string,
    endTime: string,
    bounds?: {
      north: number;
      south: number;
      east: number;
      west: number;
    },
  ) {
    const mapBounds = bounds ?? {
      north: 90,
      south: -90,
      east: 180,
      west: -180,
    };

    const params: {
      bike_ids: string[];
      start_time: string;
      end_time: string;
      grid_size: number;
      north: number;
      south: number;
      east: number;
      west: number;
    } = {
      bike_ids: bikeIds,
      start_time: startTime,
      end_time: endTime,
      grid_size: 0.001, // ~100m grid for route density
      ...mapBounds,
    };

    return this.getHeatmapData(params);
  }

  // Client-side heatmap processing utilities
  generateClientSideHeatmap(
    routes: Array<{
      coordinates: Array<{
        latitude: number;
        longitude: number;
        timestamp: string;
      }>;
    }>,
    gridSize: number = 0.001,
  ): HeatmapData {
    const pointMap = new Map<
      string,
      { lat: number; lng: number; count: number }
    >();

    // Process all route coordinates
    routes.forEach((route) => {
      route.coordinates.forEach((point) => {
        // Create grid-based key to aggregate nearby points
        const gridLat = Math.round(point.latitude / gridSize) * gridSize;
        const gridLng = Math.round(point.longitude / gridSize) * gridSize;
        const key = `${gridLat.toFixed(6)},${gridLng.toFixed(6)}`;

        if (pointMap.has(key)) {
          const existing = pointMap.get(key)!;
          existing.count += 1;
        } else {
          pointMap.set(key, {
            lat: gridLat,
            lng: gridLng,
            count: 1,
          });
        }
      });
    });

    // Convert to heatmap points
    const points = Array.from(pointMap.values()).map((point) => ({
      latitude: point.lat,
      longitude: point.lng,
      intensity: point.count,
    }));

    // Calculate bounds
    const latitudes = points.map((p) => p.latitude);
    const longitudes = points.map((p) => p.longitude);

    const bounds =
      points.length > 0
        ? {
            north: Math.max(...latitudes),
            south: Math.min(...latitudes),
            east: Math.max(...longitudes),
            west: Math.min(...longitudes),
          }
        : {
            north: 0,
            south: 0,
            east: 0,
            west: 0,
          };

    const maxIntensity =
      points.length > 0 ? Math.max(...points.map((p) => p.intensity)) : 0;

    return {
      points,
      bounds,
      maxIntensity,
      totalPoints: points.length,
    };
  }

  // Optimize heatmap data for better performance
  optimizeHeatmapData(
    heatmapData: HeatmapData,
    maxPoints: number = 5000,
    minIntensity: number = 2,
  ): HeatmapData {
    let points = heatmapData.points
      .filter((point) => point.intensity >= minIntensity)
      .sort((a, b) => b.intensity - a.intensity);

    // Limit number of points for performance
    if (points.length > maxPoints) {
      points = points.slice(0, maxPoints);
    }

    const maxIntensity =
      points.length > 0 ? Math.max(...points.map((p) => p.intensity)) : 0;

    return {
      ...heatmapData,
      points,
      maxIntensity,
      totalPoints: points.length,
    };
  }

  // City endpoints
  async getCities(params?: {
    page?: number;
    limit?: number;
    city_name?: string;
    country_code?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/cities${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async getAllCities(params?: {
    city_name?: string;
    country_code?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }) {
    const cities = await this.fetchAllPaginated<unknown>(
      async (page, limit) => {
        const response = (await this.getCities({
          ...params,
          page,
          limit,
        })) as {
          cities: unknown[];
          pagination?: { totalPages?: number };
        };

        return {
          items: response.cities || [],
          totalPages: response.pagination?.totalPages ?? 1,
        };
      },
    );

    return {
      cities,
      pagination: {
        total: cities.length,
        page: 1,
        limit: cities.length || 1,
        totalPages: 1,
      },
    };
  }

  // Country endpoints
  async getCountries(params?: {
    page?: number;
    limit?: number;
    country_code?: string;
    country_name?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return this.request(
      `/countries${queryParams.toString() ? `?${queryParams}` : ""}`,
    );
  }

  async getAllCountries(params?: {
    country_code?: string;
    country_name?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }) {
    const countries = await this.fetchAllPaginated<unknown>(
      async (page, limit) => {
        const response = (await this.getCountries({
          ...params,
          page,
          limit,
        })) as {
          countries: unknown[];
          pagination?: { totalPages?: number };
        };

        return {
          items: response.countries || [],
          totalPages: response.pagination?.totalPages ?? 1,
        };
      },
    );

    return {
      countries,
      pagination: {
        total: countries.length,
        page: 1,
        limit: countries.length || 1,
        totalPages: 1,
      },
    };
  }

  async getCountry(countryCode: string) {
    return this.request(`/countries/${countryCode}`);
  }

  async createCountry(country: {
    country_code: string;
    country_name: string;
    status?: CountryStatus;
    geom?: string | null;
  }) {
    return this.request("/countries", {
      method: "POST",
      body: JSON.stringify(country),
    });
  }

  async updateCountry(
    countryCode: string,
    updates: {
      country_name?: string;
      status?: CountryStatus;
      geom?: string | null;
    },
  ) {
    return this.request(`/countries/${countryCode}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteCountry(countryCode: string) {
    return this.request(`/countries/${countryCode}`, {
      method: "DELETE",
    });
  }

  // Vehicle category endpoints
  async getVehicleCategories(params?: {
    page?: number;
    limit?: number;
    category_id?: string;
    category_name?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return this.request(
      `/vehicle-categories${queryParams.toString() ? `?${queryParams}` : ""}`,
    );
  }

  async getAllVehicleCategories(params?: {
    category_id?: string;
    category_name?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }) {
    const categories = await this.fetchAllPaginated<unknown>(
      async (page, limit) => {
        const response = (await this.getVehicleCategories({
          ...params,
          page,
          limit,
        })) as {
          categories: unknown[];
          pagination?: { totalPages?: number };
        };

        return {
          items: response.categories || [],
          totalPages: response.pagination?.totalPages ?? 1,
        };
      },
    );

    return {
      categories,
      pagination: {
        total: categories.length,
        page: 1,
        limit: categories.length || 1,
        totalPages: 1,
      },
    };
  }

  async createVehicleCategory(category: {
    category_id: string;
    category_name: string;
    marker_image?: string | null;
    color?: string | null;
  }) {
    return this.request("/vehicle-categories", {
      method: "POST",
      body: JSON.stringify(category),
    });
  }

  async updateVehicleCategory(
    categoryId: string,
    updates: {
      category_id?: string;
      category_name?: string;
      marker_image?: string | null;
      color?: string | null;
    },
  ) {
    return this.request(`/vehicle-categories/${categoryId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteVehicleCategory(categoryId: string) {
    return this.request(`/vehicle-categories/${categoryId}`, {
      method: "DELETE",
    });
  }

  async getCity(cityId: string) {
    return this.request(`/cities/${cityId}`);
  }

  async createCity(city: {
    city_id: string;
    city_name: string;
    country_code: string;
  }) {
    return this.request("/cities", {
      method: "POST",
      body: JSON.stringify(city),
    });
  }

  async updateCity(
    cityId: string,
    updates: {
      city_name?: string;
      country_code?: string;
    },
  ) {
    return this.request(`/cities/${cityId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteCity(cityId: string) {
    return this.request(`/cities/${cityId}`, {
      method: "DELETE",
    });
  }

  // Campaign endpoints
  async getCampaigns(params?: {
    page?: number;
    limit?: number;
    search?: string;
    start_date?: string;
    end_date?: string;
    bike_id?: string;
    city_id?: string;
    status?: string;
    sort_by?: string;
    sort_order?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/campaigns${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request(endpoint);
  }

  async getCampaign(campaignId: string) {
    return this.request(`/campaigns/${campaignId}`);
  }

  async createCampaign(campaign: {
    campaign_id: string;
    start_date: string;
    end_date: string;
    bike_ids: string;
    city_ids: string;
  }) {
    return this.request("/campaigns", {
      method: "POST",
      body: JSON.stringify(campaign),
    });
  }

  async updateCampaign(
    campaignId: string,
    updates: {
      start_date?: string;
      end_date?: string;
      bike_ids?: string;
      city_ids?: string;
    },
  ) {
    return this.request(`/campaigns/${campaignId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteCampaign(campaignId: string) {
    return this.request(`/campaigns/${campaignId}`, {
      method: "DELETE",
    });
  }

  // Impression Job endpoints
  async createImpressionJob(request: {
    campaign_id: string;
  }): Promise<ImpressionJob> {
    return this.request<ImpressionJob>("/impression-jobs", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async getImpressionJobs(params?: {
    campaign_id?: string;
    status?: string;
    city?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<ImpressionJobListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/impression-jobs${
      queryParams.toString() ? `?${queryParams}` : ""
    }`;
    return this.request<ImpressionJobListResponse>(endpoint);
  }

  async getImpressionJobById(jobId: number): Promise<ImpressionJob> {
    return this.request<ImpressionJob>(`/impression-jobs/${jobId}`);
  }

  async refreshImpressionJobStatus(jobId: number): Promise<ImpressionJob> {
    return this.request<ImpressionJob>(`/impression-jobs/${jobId}/refresh`, {
      method: "POST",
    });
  }

  async getImpressionJobAnalytics(jobId: number): Promise<JobAnalytics> {
    return this.request<JobAnalytics>(`/impression-jobs/${jobId}/analytics`);
  }

  async deleteImpressionJob(jobId: number): Promise<void> {
    return this.request<void>(`/impression-jobs/${jobId}`, {
      method: "DELETE",
    });
  }

  async getImpressionJobStats(): Promise<ImpressionJobStats> {
    return this.request<ImpressionJobStats>("/impression-jobs/stats");
  }

  async downloadImpressionJobAnalytics(jobId: number): Promise<{
    download_url: string;
    file_name: string;
    expires_in_seconds: number;
  }> {
    return this.request<{
      download_url: string;
      file_name: string;
      expires_in_seconds: number;
    }>(`/impression-jobs/${jobId}/analytics/download`);
  }

  // Report Job endpoints
  async createReportJob(request: CreateReportJobRequest): Promise<ReportJob> {
    return this.request<ReportJob>("/report-jobs", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async getReportJobs(params?: {
    status?: string;
    export_type?: string;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<ReportJobListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return this.request<ReportJobListResponse>(
      `/report-jobs${queryParams.toString() ? `?${queryParams}` : ""}`,
    );
  }

  async getReportJobById(jobId: number): Promise<ReportJob> {
    return this.request<ReportJob>(`/report-jobs/${jobId}`);
  }

  async refreshReportJobStatus(jobId: number): Promise<ReportJob> {
    return this.request<ReportJob>(`/report-jobs/${jobId}/refresh`, {
      method: "POST",
    });
  }

  async retryReportJob(jobId: number): Promise<ReportJob> {
    return this.request<ReportJob>(`/report-jobs/${jobId}/retry`, {
      method: "POST",
    });
  }

  async deleteReportJob(jobId: number): Promise<void> {
    return this.request<void>(`/report-jobs/${jobId}`, {
      method: "DELETE",
    });
  }

  async downloadReportJob(jobId: number): Promise<{
    download_url: string;
    file_name: string;
    expires_in_seconds: number;
  }> {
    return this.request<{
      download_url: string;
      file_name: string;
      expires_in_seconds: number;
    }>(`/report-jobs/${jobId}/download`);
  }

  async getFleets(): Promise<{ fleets: Fleet[] }> {
    return this.request("/fleets");
  }

  async getFleetById(fleetId: string): Promise<Fleet> {
    return this.request(`/fleets/${fleetId}`);
  }

  async createFleet(data: {
    name: string;
    bike_ids?: string[];
  }): Promise<Fleet> {
    return this.request("/fleets", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateFleet(
    fleetId: string,
    data: { name: string; bike_ids: string[] },
  ): Promise<Fleet> {
    return this.request(`/fleets/${fleetId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    email?: string;
    fleet_id?: string;
    is_active?: boolean;
  }): Promise<{
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== "") {
          queryParams.append(key, String(value));
        }
      });
    }
    const qs = queryParams.toString();
    return this.request(`/auth/users${qs ? `?${qs}` : ""}`);
  }

  async updateUser(
    userId: string,
    data: Partial<{
      email: string;
      full_name: string;
      role: UserRole;
      fleet_id: string | null;
      is_active: boolean;
    }>,
  ): Promise<User> {
    return this.request(`/auth/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deactivateUser(userId: string): Promise<void> {
    return this.request(`/auth/users/${userId}`, {
      method: "DELETE",
    });
  }

  async resetUserPassword(
    userId: string,
    password?: string,
  ): Promise<{ temporary_password?: string }> {
    return this.request(`/auth/users/${userId}/reset-password`, {
      method: "POST",
      body: JSON.stringify(password ? { password } : {}),
    });
  }

  async createFleetUser(data: {
    email: string;
    full_name: string;
    fleet_id?: string;
    role?: UserRole;
    password?: string;
  }): Promise<CreateFleetUserResponse> {
    return this.request("/auth/users", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }
}

export const apiService = new ApiService();

class ApiError extends Error {
  statusCode: number;
  timestamp: string;

  constructor(message: string, statusCode: number, timestamp: string) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.timestamp = timestamp;
  }
}

export { ApiError };
